import React from 'react';
import PropTypes from 'prop-types';
import Dropdown from '../Dropdown/Dropdown';
import { GET_COMPANY_FIRM_QUERY } from '../Graphql/CompanyQueries/allCompanyQuery';
import { fetchData } from '../Service/graphQlService';

/**
 * Company selector that lets the user pick from existing companies while
 * persisting the selected company's NAME string (matching the investor
 * `company_name` data model). Unlike SelectFirm - which stores a firm id -
 * this keeps the value a plain name, so existing free-text company names keep
 * working and downstream consumers of `company_name` are unaffected.
 *
 * The dropdown is free-text enabled (freeSolo): if a company isn't in the
 * list, the user can type its name manually and it commits on Enter or blur.
 * A typed name that matches an existing company (ignoring case) snaps to that
 * company so duplicates aren't created and its address details auto-fill.
 */
class SelectCompany extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            items: [],
        };

        this.handleChange = this.handleChange.bind(this);
    }

    componentDidMount() {
        this.fetch();
    }

    fetch() {
        fetchData(GET_COMPANY_FIRM_QUERY, { type: 'Firm' })
            .then((res) => {
                const companies =
                    res && res.data && res.data.getCompanies && res.data.getCompanies.data
                        ? res.data.getCompanies.data
                        : [];

                // Keep only companies with a name, dedupe by name and sort A-Z.
                const seen = new Set();
                const items = companies
                    .filter((c) => c && c.name && c.name.trim())
                    .filter((c) => {
                        const key = c.name.trim().toLowerCase();
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    })
                    .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));

                this.setState({ items });
            })
            .catch(() => {
                this.setState({ items: [] });
            });
    }

    handleChange(data, optionsType, x, valueKey, typeKey, reason, previousData) {
        const key = this.props.valueKey;

        // A manually typed name arrives as a plain string. Trim it, and if it
        // matches an existing company (ignoring case) use that company instead
        // so its canonical name is stored and its address details auto-fill.
        if (typeof reason === 'string') {
            const typed = reason.trim();
            const match = this.state.items.find(
                (c) => c.name.trim().toLowerCase() === typed.toLowerCase()
            );

            data[key] = match ? match.name : typed || null;
            reason = match || typed;
        }

        this.props.handleParentChange(data, optionsType, x, valueKey, typeKey, reason, previousData);
    }

    render() {
        const { items } = this.state;
        const { value, ...rest } = this.props;

        // Preserve display of a legacy company name that isn't in the company
        // list so existing investors don't appear to lose their company.
        let options = items;
        if (
            value &&
            !items.some((c) => c.name && c.name.toLowerCase() === value.toLowerCase())
        ) {
            options = [{ name: value }, ...items];
        }

        return (
            <Dropdown
                optionsType="firm"
                optionKey="name"
                options={options}
                value={value}
                customValues
                autoSelect
                {...rest}
                handleParentChange={this.handleChange}
            />
        );
    }
}

SelectCompany.propTypes = {
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    label: PropTypes.string,
    data: PropTypes.object,
    handleParentChange: PropTypes.func.isRequired,
    viewMode: PropTypes.bool,
    deleted: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
};

export default SelectCompany;
